/**
 * Hotels booking widget.
 *
 * Designed to pair with an MCP server toolset that returns:
 * - `_meta.openai/outputTemplate` → `ui://widget/hotels-booking.html`
 * - `structuredContent` with `kind` discriminators (searchResults, hotelDetails, booking)
 *
 * The widget can also call MCP tools directly via `window.openai.callTool`.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@openai/apps-sdk-ui/components/Badge";
import { Button } from "@openai/apps-sdk-ui/components/Button";
import { CodeBlock } from "@openai/apps-sdk-ui/components/CodeBlock";
import { useOpenAiGlobal } from "../use-openai-global";
import { useWidgetState } from "../use-widget-state";

type NuiteeOccupancy = {
  adults: number;
  children?: number[]; // ages
};

type NuiteeSearchRequest = {
  occupancies: NuiteeOccupancy[];
  currency?: string;
  guestNationality?: string;
  checkin: string;
  checkout: string;
  countryCode?: string;
  cityName: string;
  hotelName: string;
  starRating?: number[];
  limit?: number;
  offset?: number;
};

type NuiteeRate = {
  name?: string;
  maxOccupancy?: number;
  adultCount?: number;
  childCount?: number;
  boardName?: string;
  retailRate?: { total?: Array<{ amount?: number; currency?: string }> };
  taxesAndFees?: Array<{
    included?: boolean;
    description?: string;
    amount?: number;
    currency?: string;
  }>;
};

type NuiteeRoom = {
  offerId?: string;
  rates?: NuiteeRate[];
  photos?: Array<{ url?: string }>;
  rateId?: string;
  mappedRoomId?: number;
  paymentTypes?: string[];
  refundableTag?: string;
};

type NuiteeHotel = {
  hotelId: string;
  name: string;
  description?: string;
  main_photo?: string;
  address?: string;
  hotelPageLink?: string;
  bookingLink?: string;
  rooms?: NuiteeRoom[];
  [key: string]: unknown;
};

type SearchResultsPayload = {
  kind: "searchResults";
  sessionId: string;
  request: NuiteeSearchRequest;
  response: { hotels: NuiteeHotel[]; [key: string]: unknown };
  updatedAt: string;
};

type HotelDetailsPayload = {
  kind: "hotelDetails";
  sessionId: string;
  hotel: NuiteeHotel;
  updatedAt: string;
};

type BookingPayload = {
  kind: "booking";
  sessionId: string;
  booking: {
    id: string;
    status: "pending" | "confirmed" | "cancelled";
    hotelId: string;
    hotelName: string;
    bookingLink?: string;
    offerId?: string;
    rateId?: string;
    notes?: string;
  };
  updatedAt: string;
};

type ToolOutput = SearchResultsPayload | HotelDetailsPayload | BookingPayload;

type HotelsWidgetState = {
  sessionId: string | null;
  lastRequest: NuiteeSearchRequest | null;
  lastResults: SearchResultsPayload | null;
  lastHotelDetails: HotelDetailsPayload | null;
  lastBooking: BookingPayload | null;
};

function formatMoney(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(0)}`;
  }
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border border-default rounded-2xl bg-surface shadow-sm p-4 flex flex-col gap-3">
      <header className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-primary">{title}</h2>
        {subtitle ? <p className="text-sm text-secondary">{subtitle}</p> : null}
      </header>
      {children}
    </section>
  );
}

function nightsBetween(checkin: string, checkout: string) {
  const a = new Date(`${checkin}T00:00:00`);
  const b = new Date(`${checkout}T00:00:00`);
  const diff = Math.round((b.getTime() - a.getTime()) / 86_400_000);
  return Math.max(1, Number.isFinite(diff) ? diff : 1);
}

function cheapestTotal(hotel: NuiteeHotel): { amount: number; currency: string } | null {
  const rooms = hotel.rooms ?? [];
  let best: { amount: number; currency: string } | null = null;
  for (const room of rooms) {
    for (const rate of room.rates ?? []) {
      const total = rate.retailRate?.total?.[0];
      const amount = typeof total?.amount === "number" ? total.amount : null;
      const currency = typeof total?.currency === "string" ? total.currency : null;
      if (amount == null || currency == null) continue;
      if (!best || amount < best.amount) best = { amount, currency };
    }
  }
  return best;
}

function htmlDescriptionToText(input?: string): string {
  const html = typeof input === "string" ? input.trim() : "";
  if (!html) return "";

  // Fast path: already plain text.
  if (!/[<>]/.test(html)) return html;

  try {
    if (typeof window !== "undefined" && "DOMParser" in window) {
      const doc = new window.DOMParser().parseFromString(html, "text/html");
      const paragraphs = Array.from(doc.body.querySelectorAll("p"));
      if (paragraphs.length > 0) {
        const parts = paragraphs
          .map((p) => (p as HTMLElement).innerText?.trim() ?? "")
          .filter(Boolean)
          .map((t) => t.replace(/\n+/g, " — "));
        return parts.join("\n\n").trim();
      }
      const fallback = (doc.body as HTMLElement).innerText?.trim() ?? "";
      return fallback.replace(/\n+/g, " — ").trim();
    }
  } catch {
    // fall through
  }

  // Very small fallback: strip tags and normalize whitespace.
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p>/gi, "\n\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

export default function HotelsBooking() {
  const toolOutputRaw = (useOpenAiGlobal("toolOutput") ?? null) as
    | ToolOutput
    | null;
  const toolInput = (useOpenAiGlobal("toolInput") ??
    null) as Record<string, unknown> | null;
  const toolResponseMetadata = (useOpenAiGlobal("toolResponseMetadata") ??
    null) as Record<string, unknown> | null;

  const [widgetState, setWidgetState] = useWidgetState<HotelsWidgetState>({
    sessionId: null,
    lastRequest: null,
    lastResults: null,
    lastHotelDetails: null,
    lastBooking: null,
  });

  const [isBusy, setIsBusy] = useState(false);
  const [lastCallResult, setLastCallResult] = useState<string | null>(null);
  const carouselRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!toolOutputRaw) return;

    void setWidgetState((prev) => {
      const next: HotelsWidgetState = {
        ...(prev ?? {
          sessionId: null,
          lastQuery: null,
          lastResults: null,
          lastHotelDetails: null,
          lastBooking: null,
        }),
      };

      if (toolOutputRaw.kind === "searchResults") {
        next.sessionId = toolOutputRaw.sessionId;
        next.lastRequest = toolOutputRaw.request;
        next.lastResults = toolOutputRaw;
      } else if (toolOutputRaw.kind === "hotelDetails") {
        next.sessionId = toolOutputRaw.sessionId;
        next.lastHotelDetails = toolOutputRaw;
      } else if (toolOutputRaw.kind === "booking") {
        next.sessionId = toolOutputRaw.sessionId;
        next.lastBooking = toolOutputRaw;
      }

      return next;
    });
  }, [toolOutputRaw, setWidgetState]);

  const derived = useMemo(() => {
    const lastResults = widgetState?.lastResults ?? null;
    const lastDetails = widgetState?.lastHotelDetails ?? null;
    const lastBooking = widgetState?.lastBooking ?? null;
    const sessionId = widgetState?.sessionId ?? null;
    return { lastResults, lastDetails, lastBooking, sessionId };
  }, [widgetState?.lastBooking, widgetState?.lastHotelDetails, widgetState?.lastResults, widgetState?.sessionId]);

  const callTool = async (name: string, args: Record<string, unknown>) => {
    if (!window.openai?.callTool) {
      return null;
    }
    setIsBusy(true);
    setLastCallResult(null);
    try {
      const response = await window.openai.callTool(name, args);
      const serialized =
        typeof response === "string" ? response : JSON.stringify(response, null, 2);
      setLastCallResult(serialized);
      return response;
    } catch (e) {
      return null;
    } finally {
      setIsBusy(false);
    }
  };

  const openBookingLink = (href?: string) => {
    const url = typeof href === "string" ? href : "";
    if (!url) return;
    if (window.openai?.openExternal) {
      window.openai.openExternal({ href: url });
      return;
    }
    // Fallback for environments that don't expose openExternal.
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleCancel = async () => {
    const sessionId = derived.sessionId ?? undefined;
    const bookingId = derived.lastBooking?.booking.id;
    if (!bookingId) return;
    await callTool("hotels-cancel", { bookingId, sessionId });
  };

  const hotels = derived.lastResults?.response.hotels ?? [];
  const nights = derived.lastResults?.request
    ? nightsBetween(derived.lastResults.request.checkin, derived.lastResults.request.checkout)
    : 1;

  const scrollCarousel = (dir: -1 | 1) => {
    const el = carouselRef.current;
    if (!el) return;
    const firstCard = el.querySelector<HTMLElement>("[data-hb-card]");
    const delta = (firstCard?.offsetWidth ?? 360) + 12;
    el.scrollBy({ left: dir * delta, behavior: "smooth" });
  };

  const skeletonCards = useMemo(() => Array.from({ length: 6 }, (_, i) => i), []);

  return (
    <div>
          <Section
        title="Results"
        subtitle={
          derived.lastResults
            ? `${hotels.length} hotel(s)`
            : ""
        }
      >
        {hotels.length === 0 ? (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Button variant="outline" color="secondary" disabled>
                  Prev
                </Button>
                <Button variant="outline" color="secondary" disabled>
                  Next
                </Button>
              </div>
            </div>

            <div className="hb-carousel hb-scroll" aria-label="Loading hotels">
              {skeletonCards.map((i) => (
                <div
                  key={i}
                  className="hb-carousel-card hb-skeleton-card border border-default rounded-2xl bg-surface overflow-hidden flex flex-col"
                >
                  <div className="h-36 bg-subtle border-b border-default hb-skeleton-block" />
                  <div className="p-3 flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="hb-skeleton-line hb-skeleton-shimmer w-3/4" />
                        <div className="hb-skeleton-line hb-skeleton-shimmer w-1/2 mt-2" />
                      </div>
                      <div className="text-right shrink-0">
                        <div className="hb-skeleton-line hb-skeleton-shimmer w-16" />
                        <div className="hb-skeleton-line hb-skeleton-shimmer w-20 mt-2" />
                      </div>
                    </div>
                    <div className="hb-skeleton-line hb-skeleton-shimmer w-full" />
                    <div className="hb-skeleton-line hb-skeleton-shimmer w-5/6" />
                    <div className="flex flex-wrap gap-2 pt-1">
                      <div className="hb-skeleton-pill hb-skeleton-shimmer w-32" />
                      <div className="hb-skeleton-pill hb-skeleton-shimmer w-24" />
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <div className="hb-skeleton-button hb-skeleton-shimmer w-40" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  color="secondary"
                  onClick={() => scrollCarousel(-1)}
                  disabled={isBusy}
                >
                  Prev
                </Button>
                <Button
                  variant="outline"
                  color="secondary"
                  onClick={() => scrollCarousel(1)}
                  disabled={isBusy}
                >
                  Next
                </Button>
              </div>
            </div>

            <div ref={carouselRef} className="hb-carousel hb-scroll">
            {hotels.map((h) => {
              const best = cheapestTotal(h);
              const perNight =
                best && nights > 0 ? { amount: best.amount / nights, currency: best.currency } : null;
              return (
                <div
                  key={h.hotelId}
                  data-hb-card
                  className="hb-carousel-card border border-default rounded-2xl bg-surface overflow-hidden flex flex-col"
                >
                  <div className="h-36 bg-subtle border-b border-default">
                    {h.main_photo ? (
                      <img
                        src={h.main_photo}
                        alt={h.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-secondary">
                        Photo
                      </div>
                    )}
                  </div>

                  <div className="p-3 flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-primary truncate">
                          {h.name}
                        </h3>
                        <p className="text-xs text-secondary truncate">
                          {h.address ?? ""}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        {best ? (
                          <>
                            <p className="text-sm font-semibold text-primary">
                              {formatMoney(best.amount, best.currency)}
                            </p>
                            <p className="text-[11px] text-secondary">
                              total ·{" "}
                              {perNight
                                ? `${formatMoney(perNight.amount, perNight.currency)}/night`
                                : "per night"}
                            </p>
                          </>
                        ) : (
                          <p className="text-sm text-secondary">No price</p>
                        )}
                      </div>
                    </div>

                    {h.description ? (
                      <p className="text-xs text-secondary line-clamp-3 whitespace-pre-line">
                        {htmlDescriptionToText(h.description)}
                      </p>
                    ) : null}

                    <div className="flex flex-wrap gap-2 items-center">
                      <Badge variant="soft" color="secondary" pill>
                        id: {h.hotelId}
                      </Badge>
                      {typeof h.rooms?.length === "number" ? (
                        <Badge variant="soft" color="info" pill>
                          {h.rooms.length} offer(s)
                        </Badge>
                      ) : null}
                    </div>

                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        color="primary"
                        onClick={() => openBookingLink(h.bookingLink)}
                        disabled={isBusy || !h.bookingLink}
                      >
                        View on Marriott
                      </Button>
                      {h.hotelPageLink && window.openai?.openExternal ? (
                        <Button
                          variant="outline"
                          color="secondary"
                          onClick={() =>
                            window.openai?.openExternal?.({
                              href: h.hotelPageLink as string,
                            })
                          }
                          disabled={isBusy}
                        >
                          Open hotel page
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}
      </Section>
      <div className="hb-hide bg-surface border border-default rounded-2xl shadow-sm p-4 flex flex-col gap-4 text-primary">
    

      <Section title="Selected hotel" subtitle="Details returned by hotels-details.">
        {derived.lastDetails ? (
          (() => {
            const details = derived.lastDetails;
            const best = cheapestTotal(details.hotel);
            return (
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="soft" color="discovery" pill>
                    {details.hotel.name}
                  </Badge>
                  <Badge variant="soft" color="secondary" pill>
                    id: {details.hotel.hotelId}
                  </Badge>
                  {best ? (
                    <Badge variant="soft" color="info" pill>
                      from {formatMoney(best.amount, best.currency)} total
                    </Badge>
                  ) : null}
                </div>
                {details.hotel.description ? (
                  <p className="text-sm text-secondary leading-snug whitespace-pre-line">
                    {htmlDescriptionToText(details.hotel.description)}
                  </p>
                ) : null}
                {details.hotel.address ? (
                  <p className="text-sm text-secondary">{details.hotel.address}</p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {details.hotel.hotelPageLink && window.openai?.openExternal ? (
                    <Button
                      variant="outline"
                      color="secondary"
                      onClick={() =>
                        window.openai?.openExternal?.({
                          href: details.hotel.hotelPageLink as string,
                        })
                      }
                    >
                      Open hotel page
                    </Button>
                  ) : null}
                  {details.hotel.bookingLink && window.openai?.openExternal ? (
                    <Button
                      color="primary"
                      onClick={() =>
                        window.openai?.openExternal?.({
                          href: details.hotel.bookingLink as string,
                        })
                      }
                    >
                      Open booking link
                    </Button>
                  ) : null}
                </div>
                {typeof details.hotel.rooms?.length === "number" ? (
                  <p className="text-xs text-secondary">
                    Offers: {details.hotel.rooms.length}
                  </p>
                ) : null}
              </div>
            );
          })()
        ) : (
          <p className="text-sm text-secondary">
            No hotel selected yet. Click “View details” on a result card.
          </p>
        )}
      </Section>

      <Section title="Booking" subtitle="Booking status returned by hotels-book / hotels-cancel.">
        {derived.lastBooking ? (
          (() => {
            const booking = derived.lastBooking;
            return (
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant="solid"
                    color={
                      booking.booking.status === "confirmed"
                        ? "success"
                        : booking.booking.status === "cancelled"
                          ? "danger"
                          : "secondary"
                    }
                    pill
                  >
                    {booking.booking.status.toUpperCase()}
                  </Badge>
                  <span className="text-sm font-semibold text-primary">
                    {booking.booking.hotelName}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="border border-default rounded-xl bg-subtle p-3">
                    <p className="text-[11px] uppercase tracking-[0.12em] text-secondary font-semibold">
                      Booking id
                    </p>
                    <p className="text-sm text-primary font-mono">{booking.booking.id}</p>
                  </div>
                  <div className="border border-default rounded-xl bg-subtle p-3">
                    <p className="text-[11px] uppercase tracking-[0.12em] text-secondary font-semibold">
                      Selection
                    </p>
                    <p className="text-sm text-primary">
                      {booking.booking.offerId
                        ? `offerId: ${booking.booking.offerId}`
                        : "offerId: —"}
                      {" · "}
                      {booking.booking.rateId
                        ? `rateId: ${booking.booking.rateId}`
                        : "rateId: —"}
                    </p>
                  </div>
                </div>
                {booking.booking.bookingLink ? (
                  <div className="border border-default rounded-xl bg-subtle p-3">
                    <p className="text-[11px] uppercase tracking-[0.12em] text-secondary font-semibold">
                      Booking link
                    </p>
                    <p className="text-xs text-secondary break-all">
                      {booking.booking.bookingLink}
                    </p>
                    {window.openai?.openExternal ? (
                      <Button
                        color="primary"
                        className="mt-2 w-fit"
                        onClick={() =>
                          window.openai?.openExternal?.({
                            href: booking.booking.bookingLink as string,
                          })
                        }
                      >
                        Open booking
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-sm text-secondary">
                    No booking link available for this booking.
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    color="danger"
                    onClick={handleCancel}
                    disabled={isBusy || booking.booking.status === "cancelled"}
                  >
                    Cancel booking
                  </Button>
                </div>
              </div>
            );
          })()
        ) : (
          <p className="text-sm text-secondary">
            No booking yet. Click “Book” on a hotel result.
          </p>
        )}
      </Section>

      <Section title="Debug" subtitle="Tool I/O and the last callTool() response.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex flex-col gap-2">
            <p className="text-xs uppercase tracking-[0.12em] text-secondary font-semibold">
              window.openai.toolInput
            </p>
            <CodeBlock className="hb-codeblock" language="json">
              {JSON.stringify(toolInput ?? {}, null, 2)}
            </CodeBlock>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-xs uppercase tracking-[0.12em] text-secondary font-semibold">
              toolOutput (structuredContent)
            </p>
            <CodeBlock className="hb-codeblock" language="json">
              {JSON.stringify(toolOutputRaw ?? {}, null, 2)}
            </CodeBlock>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-xs uppercase tracking-[0.12em] text-secondary font-semibold">
            toolResponseMetadata / last callTool() response
          </p>
          <CodeBlock className="hb-codeblock" language="json">
            {JSON.stringify(
              { toolResponseMetadata, lastCallResult: lastCallResult ?? null },
              null,
              2
            )}
          </CodeBlock>
        </div>
      </Section>
    </div>
    </div>
    
  );
}


